import React, { useRef, useState } from 'react';

import cn from 'classnames';
import dayjs from 'dayjs';
import Link from 'next/link';

import { NEWS_PATHS } from '@constants/index'
import { useIsomorphicLayoutEffect } from '@lib/hooks'
import I18n from '@utils/i18n';
import ActionGroup from '@widget/post/Details/Components/ActionGroup';

import { Market } from '../../Market';
import { useAsideHeight } from '../hooks/useAsideHeight'
import { HotNews } from '../HotNews'
import { NewsLayout } from '../Layout/index'

import styles from './index.module.less';

const getVerbText = (verb) => {
  const VERB_MAP = {
    NEWS_FLASH: I18n.t('7RrmgNLW'),
    STORY: I18n.t('YwtZDqqk')
  }
  return VERB_MAP[verb] || VERB_MAP.NEWS_FLASH
}

const Header = ({ post = {}, headerRef }) => {
  useIsomorphicLayoutEffect(() => {
    const resizeObserver = new ResizeObserver(([entry]) => {
      const { contentBoxSize: [{ inlineSize }] } = entry;
      if (inlineSize < 961) {
        headerRef.current.classList.remove(styles.notBgHeader)
        return
      }
      if (!post.images?.length) {
        headerRef.current.classList.add(styles.notBgHeader)
      }
    });
    resizeObserver.observe(document.body);
    return () => {
      resizeObserver.unobserve(document.body)
    }
  }, [])
  return (
    <div
      ref={headerRef}
      className={cn(styles.header)}
      style={{ backgroundImage: `linear-gradient(179deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.6) 100%), url(${post?.images?.[0]})` }}>
      <div className={styles.content}>
        <p className={styles.p}>
          <span className={styles.type}>{getVerbText(post?.verb)}</span>
          <span className={styles.source}>{I18n.t('l7RS4AY4')}</span>
          <Link legacyBehavior href={post?.link || ''}>
            <a target="_blank" className={styles.link} data-buried={'ZX-08'}>{post?.linkText}</a>
          </Link>
        </p>
        <p className={styles.time}>
          <span className={styles.span}>{dayjs(post?.timestamp).format('YYYY-MM-DD HH:mm:ss')}</span>
        </p>
        <h1 className={styles.h1}>{post.title}</h1>
        <p className={cn(styles.copyright, 'Medium')}>{I18n.t('1wZGovMu')}</p>
      </div>
    </div>
  );
}

export const DetailLayout = (props) => {
  const { recommend, post, children, likeCount, isLoved, like, commentCount, commentInputRef, asideTag } = props;
  const headerRef = useRef()
  const layoutRef = useRef()
  const mainRef = useRef()
  const actionRef = useRef({})
  const [shareCount, setShareCount] = useState(post?.shareCount)

  const { ref: asideRef } = useAsideHeight()

  const handleScroll = () => {
    window.scrollTo({
      top: commentInputRef.current?.offsetTop,
      left: 0,
      behavior: 'smooth'
    });
    commentInputRef.current?.focus({
      cursor: 'end'
    });
  };

  useIsomorphicLayoutEffect(() => {
    const resizeObserver = new ResizeObserver(([entry]) => {
      const { contentBoxSize: [{ inlineSize }] } = entry;
      if (!post.images?.length && inlineSize > 960 && !actionRef.current?.remove) {
        const oldNode = layoutRef.current.removeChild(headerRef.current);
        const referenceNode = mainRef.current.querySelector('*:first-child')
        mainRef.current.insertBefore(oldNode, referenceNode)
        actionRef.current.remove = true
      }
      if (actionRef.current?.remove && inlineSize <= 960) {
        const oldNode = mainRef.current.removeChild(headerRef.current);
        const referenceNode = layoutRef.current.querySelector('*:first-child')
        layoutRef.current.insertBefore(oldNode, referenceNode)
        actionRef.current.remove = false
      }
    });
    resizeObserver.observe(document.body);
    return () => {
      resizeObserver.unobserve(document.body)
    }
  }, [])

  useIsomorphicLayoutEffect(() => {
    setShareCount(post?.shareCount)
  }, [post?.id])
  return (
    <NewsLayout>
      <div className={styles.layout} ref={layoutRef}>
        <Header post={post} headerRef={headerRef} />
        <div className={cn(styles.container)}>
          <ActionGroup
            itemData={post}
            curLikeCount={likeCount}
            isLoved={isLoved}
            likeFun={like}
            curShareCount={shareCount}
            setCurShareCount={setShareCount}
            commentFun={handleScroll}
            commentCount={commentCount}
            className={styles.actionGroup}
            itemClass={styles.action}
            sharePath={`${NEWS_PATHS.POST}/`}
          />
          <div className={styles.main} ref={mainRef}>
            {children}
          </div>
          <div className={styles.aside} ref={asideRef}>
            <HotNews recommend={recommend} asideTag={asideTag} />
            <Market />
          </div>
        </div>
      </div>
    </NewsLayout>
  );
};
